/* eslint-disable no-prototype-builtins */

import { isPlainObject } from 'lodash';

type AnyObject = Record<string | number | symbol, unknown>;
type Path = string;

export const objectKeys = <T extends object>(o: NonNullable<T>): (keyof T)[] => {
  return Object.keys(o) as (keyof T)[];
};

export const objectEntries = <T extends object>(o: NonNullable<T>): [keyof T, T[keyof T]][] => {
  return Object.entries(o) as [keyof T, T[keyof T]][];
};

const getObjectPathsRecursively = (obj: AnyObject, prefix = ''): string[] =>
  objectKeys(obj).reduce((acc: string[], key) => {
    const value = obj[key];

    if (typeof value === 'object' && value !== null) {
      return [...acc, ...getObjectPathsRecursively(value as AnyObject, `${prefix}${String(key)}.`)];
    }

    return [...acc, `${prefix}${String(key)}`];
  }, []);

export const getObjectPaths = (source: any): string[] => getObjectPathsRecursively(source);

export const flattenObject = (input: Record<string | number | symbol, any>): Record<Path, string> => {
  const toReturn: Record<Path, any> = {};

  for (const property in input) {
    if (!input.hasOwnProperty(property)) {
      continue;
    }

    if (typeof input[property] == 'object' && input[property] !== null) {
      const flatObject = flattenObject(input[property]);
      for (const key in flatObject) {
        if (!flatObject.hasOwnProperty(key)) {
          continue;
        }

        toReturn[property + '.' + key] = flatObject[key];
      }
    } else {
      toReturn[property] = input[property];
    }
  }
  return toReturn;
};

const sortObject = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map((item) => sortObject(item)) as T;
  }

  if (isPlainObject(obj) === false) {
    return obj;
  }

  return objectKeys(obj as object)
    .sort()
    .reduce((result, key) => {
      result[key] = sortObject(obj[key]);
      return result;
    }, {} as T);
};

const safeBufferMessage = async (message: string): Promise<ArrayBuffer> => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    // Browser environment
    const msgBuffer = new TextEncoder().encode(message);
    return await crypto.subtle.digest('SHA-256', msgBuffer);
  }

  if (typeof require === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const crypto = require('crypto');
    return crypto.createHash('sha256').update(message).digest();
  }

  throw new Error('Unsupported environment');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hashObject = async <T extends Record<string, any>>(obj: T): Promise<string> => {
  const message = JSON.stringify(sortObject(obj));

  const hashBuffer = await safeBufferMessage(message);

  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};
